import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import DetailActions from "@/components/feature/DetailActions";
import EmptyState from "@/components/base/EmptyState";
import { reviews, getReviewById } from "@/mocks/reviews";
import { getMovieById } from "@/mocks/movies";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function ReviewDetail() {
  const { id } = useParams<{ id: string }>();
  const review = id ? getReviewById(id) : undefined;

  const related = useMemo(() => {
    if (!review) return [];
    return reviews
      .filter((r) => r.id !== review.id)
      .sort((a, b) => {
        const aScore = (a.movieId === review.movieId ? 2 : 0) + a.tags.filter((t) => review.tags.includes(t)).length;
        const bScore = (b.movieId === review.movieId ? 2 : 0) + b.tags.filter((t) => review.tags.includes(t)).length;
        return bScore - aScore;
      })
      .slice(0, 3);
  }, [review]);

  useDocumentMeta(
    review ? `${review.title} | 小黄人电影` : "影评详情 | 小黄人电影",
    review ? review.summary.slice(0, 100) : undefined
  );

  if (!review) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-[1280px] px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-chat-quote-line"
            title="影评不存在"
            desc="这篇影评可能已被删除或链接有误，去影评专区看看其他内容吧。"
            actionLabel="返回影评列表"
            actionTo="/reviews"
            actionIcon="ri-arrow-left-line"
          />
        </div>
      </SiteLayout>
    );
  }

  const movie = getMovieById(review.movieId);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[860px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影评专区", to: "/reviews" },
            { label: review.author },
          ]}
        />

        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            {review.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
              >
                #{tag}
              </span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {review.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-bold text-primary-800">
                {review.author.slice(0, 1)}
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-950">{review.author}</p>
                <p className="text-xs text-foreground-500">
                  {review.authorRole} · {review.date}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-accent-500 px-4 py-2 text-sm font-bold text-background-50">
              <i className="ri-star-fill"></i>
              {review.rating} 分
            </div>
          </div>
        </div>

        {movie && (
          <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl bg-primary-50 p-4">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
              <i className="ri-film-line text-xl"></i>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-foreground-500">本篇影评讨论的影片</p>
              <p className="text-sm font-semibold text-foreground-950">
                {movie.title}（{movie.year}）
              </p>
            </div>
            <Link
              to={`/detail/${movie.id}`}
              className="flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-foreground-950 transition hover:bg-primary-400"
            >
              查看影片
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}

        <article className="mt-8 space-y-5 border-l-2 border-primary-200 pl-5">
          {review.content.map((para, index) => (
            <p key={index} className="text-[15px] leading-8 text-foreground-700">
              {para}
            </p>
          ))}
        </article>

        <div className="mt-8 rounded-2xl border border-background-200 bg-background-50 p-5">
          <DetailActions
            type="review"
            id={review.id}
            title={review.title}
            path={`/reviews/${review.id}`}
          />
        </div>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-links-line"></i>
            </span>
            相关影评推荐
          </h2>
          <ul className="mt-4 space-y-2">
            {related.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/reviews/${item.id}`}
                  className="group flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3.5 transition hover:border-primary-300"
                >
                  <i className="ri-chat-quote-line text-lg text-primary-600"></i>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-500">
                      {item.author} · 评分 {item.rating}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </SiteLayout>
  );
}